/**
 * Analytics Service
 * Application layer - Orchestrates event tracking
 * Provides high-level API for tracking events across multiple providers
 */

import type { EventTracker } from '../../domain/interfaces/EventTracker';

/**
 * Analytics Event Provider Types
 */
export type AnalyticsEventProviderType = 'ga' | 'webengage' | 'gokwik' | 'eventlogger' | 'facebook' | 'openai';

/**
 * Track Event Options
 */
export interface TrackEventOptions {
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[];
}

/**
 * Extended Event Tracker
 * Extends the base EventTracker interface to support event provider filtering
 */
export interface ExtendedEventTracker extends EventTracker {
  trackEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options?: TrackEventOptions
  ): void | Promise<void>;
}

interface GoKwikCapableTracker extends EventTracker {
  logGoKwikEvent?: (
    eventName: string,
    attributes?: Record<string, unknown>,
    eventCategory?: string
  ) => void | Promise<void>;
  logGoKwikSystemEvent?: (
    type: string,
    attributes?: Record<string, unknown>
  ) => void | Promise<void>;
}

/**
 * Analytics Service
 * Application service that provides a clean API for event tracking
 */
export class AnalyticsService {
  private readonly tracker: EventTracker;

  constructor(tracker: EventTracker) {
    this.tracker = tracker;
  }

  /**
   * Track a custom event
   * @param eventName - Name of the event
   * @param properties - Event properties/attributes
   * @param options - Optional configuration (e.g., which event providers should receive this event)
   */
  trackEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options?: TrackEventOptions
  ): void {
    if (!this.tracker) {
      console.error('AnalyticsService: tracker is not available');
      return;
    }
    
    // If options are provided, use ExtendedEventTracker signature
    // The type assertion is safe because in practice, AnalyticsService is always
    // constructed with CompositeEventTracker which implements ExtendedEventTracker
    if (options !== undefined) {
      const extendedTracker = this.tracker as ExtendedEventTracker;
      extendedTracker.trackEvent(eventName, properties, options);
      return;
    }
    
    // Fallback to base EventTracker signature (no options)
    this.tracker.trackEvent(eventName, properties);
  }

  /**
   * Track a GoKwik custom event.
   */
  logGoKwikEvent(
    eventName: string,
    attributes: Record<string, unknown> = {},
    eventCategory?: string
  ): void {
    const tracker = this.tracker as GoKwikCapableTracker;
    if (typeof tracker.logGoKwikEvent === 'function') {
      tracker.logGoKwikEvent(eventName, attributes, eventCategory);
      return;
    }
    console.warn('AnalyticsService: GoKwik tracker is not configured');
  }

  /**
   * Track a GoKwik system event.
   */
  logGoKwikSystemEvent(type: string, attributes: Record<string, unknown> = {}): void {
    const tracker = this.tracker as GoKwikCapableTracker;
    if (typeof tracker.logGoKwikSystemEvent === 'function') {
      tracker.logGoKwikSystemEvent(type, attributes);
      return;
    }
    console.warn('AnalyticsService: GoKwik tracker is not configured');
  }

  /**
   * Post a server-side event to the Bluestone eventLogger service.
   * Payload must include `_eventId`.
   */
  logEventLogger(payload: Record<string, unknown>): void {
    const tracker = this.tracker as EventTracker;
    if (typeof tracker.logEventLogger === 'function') {
      tracker.logEventLogger(payload);
      return;
    }
    console.warn('AnalyticsService: EventLogger tracker is not configured');
  }

  /**
   * Identify a logged-in user and set their profile attributes.
   * Delegates to the underlying tracker's loginUser implementation (e.g. WebEngage).
   * Called after successful login or signup.
   */
  loginUser(params: {
    userCode: string;
    email?: string;
    firstName?: string;
    phone?: string;
  }): void {
    const tracker = this.tracker as EventTracker;
    if (typeof tracker.loginUser === 'function') {
      void tracker.loginUser(params.userCode, {
        email: params.email,
        firstName: params.firstName,
        phone: params.phone,
      });
    }
  }

  /**
   * Get the underlying tracker (for debugging purposes)
   * @internal
   */
  getTracker(): EventTracker {
    return this.tracker;
  }
}

/**
 * Create Analytics Service
 * Factory function to create analytics service
 */
export function createAnalyticsService(tracker: EventTracker): AnalyticsService {
  return new AnalyticsService(tracker);
}

